<template>
  <CModal
    title="Create Promotion Code"
    :show="visible"
    :close-on-backdrop="false"
  >
    <CForm @submit.prevent="submit">
      <CAlert v-if="apiError" color="danger">{{ apiError }}</CAlert>
      <CInput
        v-model="form.code"
        label="Code"
        :invalid-feedback="errors.code"
      />
      <CSelect
        :value.sync="form.restriction"
        label="Restriction"
        :options="restrictionOptions"
      />
      <CInput
        v-model="form.limit"
        label="Limit"
        placeholder="No limit"
        :invalid-feedback="errors.limit"
      />
      <CSelect
        :value.sync="form.discountType"
        label="Discount type"
        :options="discountTypeOptions"
      />
      <CInput
        v-model="form.discountValue"
        :label="form.discountType === 'amount' ? 'Amount ($)' : 'Percentage'"
        :invalid-feedback="errors.discountValue"
      />
      <CInputCheckbox
        :checked="form.always"
        label="Always"
        @update:checked="setAlways"
      />
      <CInput
        v-model="form.effectiveFrom"
        type="datetime-local"
        label="Effective from"
        :disabled="form.always"
      />
      <CInput
        v-model="form.effectiveTo"
        type="datetime-local"
        label="Effective to"
        :disabled="form.always"
        :invalid-feedback="errors.effectiveDate"
      />
    </CForm>
    <template #footer>
      <CButton color="secondary" :disabled="saving" @click="close">
        Cancel
      </CButton>
      <CButton color="primary" :disabled="saving" @click="submit">
        {{ saving ? 'Saving...' : 'Save' }}
      </CButton>
    </template>
  </CModal>
</template>

<script>
import { ACTION_CREATE_PROMOTION_CODE } from '@/store/actions.type';

function initialForm() {
  return {
    code: '',
    restriction: 'none',
    limit: '',
    discountType: 'percentage',
    discountValue: '',
    always: true,
    effectiveFrom: '',
    effectiveTo: '',
  };
}

function parseAmountToCents(value) {
  const normalized = String(value || '').trim();
  if (!/^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/.test(normalized)) return null;
  const parts = normalized.split('.');
  const dollars = Number(parts[0]);
  const cents = Number((parts[1] || '').padEnd(2, '0'));
  const total = dollars * 100 + cents;
  return Number.isSafeInteger(total) && total > 0 ? total : null;
}

export default {
  name: 'CreatePromotionCodeModal',
  props: {
    visible: { type: Boolean, default: false },
  },
  data() {
    return {
      form: initialForm(),
      errors: {},
      apiError: '',
      saving: false,
      restrictionOptions: [
        { value: 'none', label: 'None' },
        { value: 'first_booking', label: 'First booking' },
        { value: 'one_time', label: 'One time' },
      ],
      discountTypeOptions: [
        { value: 'percentage', label: 'Percentage' },
        { value: 'amount', label: 'Amount' },
      ],
    };
  },
  watch: {
    visible(value) {
      if (value) this.reset();
    },
  },
  methods: {
    parseAmountToCents,
    reset() {
      this.form = initialForm();
      this.errors = {};
      this.apiError = '';
      this.saving = false;
    },
    close() {
      this.$emit('update:visible', false);
    },
    setAlways(value) {
      this.form.always = value;
      if (value) {
        this.form.effectiveFrom = '';
        this.form.effectiveTo = '';
      }
    },
    validate() {
      const errors = {};
      const code = String(this.form.code || '').trim();
      if (!code || !/^[A-Za-z0-9]+$/.test(code)) {
        errors.code = 'Only alphanumeric digits allowed.';
      }
      const limit = String(this.form.limit || '').trim();
      if (limit && !/^[1-9]\d*$/.test(limit)) {
        errors.limit = 'Limit must be a positive integer.';
      }
      if (this.form.discountType === 'percentage') {
        const percentage = Number(this.form.discountValue);
        if (
          !/^\d+$/.test(String(this.form.discountValue)) ||
          percentage < 1 ||
          percentage > 100
        ) {
          errors.discountValue = 'Percentage must be between 1 and 100.';
        }
      } else if (parseAmountToCents(this.form.discountValue) === null) {
        errors.discountValue =
          'Amount must be greater than $0.00 with at most two decimals.';
      }
      if (!this.form.always) {
        if (!this.form.effectiveFrom || !this.form.effectiveTo) {
          errors.effectiveDate = 'Start and end dates are required.';
        } else if (
          new Date(this.form.effectiveFrom) >= new Date(this.form.effectiveTo)
        ) {
          errors.effectiveDate = 'Start date must be before end date.';
        }
      }
      this.errors = errors;
      return errors;
    },
    buildPayload() {
      const isAmount = this.form.discountType === 'amount';
      return {
        code: String(this.form.code).trim(),
        restriction: this.form.restriction,
        limit: this.form.limit === '' ? null : Number(this.form.limit),
        discount_type: this.form.discountType,
        discount_value: isAmount
          ? parseAmountToCents(this.form.discountValue)
          : Number(this.form.discountValue),
        always: this.form.always,
        effective_from: this.form.always
          ? null
          : new Date(this.form.effectiveFrom).toISOString(),
        effective_to: this.form.always
          ? null
          : new Date(this.form.effectiveTo).toISOString(),
      };
    },
    submit() {
      this.apiError = '';
      if (Object.keys(this.validate()).length > 0) return Promise.resolve();
      this.saving = true;
      return this.$store
        .dispatch(ACTION_CREATE_PROMOTION_CODE, this.buildPayload())
        .then((response) => {
          this.$emit('created', response.data);
          this.close();
        })
        .catch((error) => {
          this.apiError =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            error.message ||
            'Unable to create promotion code.';
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>
